import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createAsset, getAsset, updateAsset } from '@/api/assets';
import { listFieldDefinitions } from '@/api/assetFieldDefinitions';
import { getLocations } from '@/api/locations';
import { ApiError } from '@/api/client';
import { assetSchema, assetCategories, lifecycleStates, type AssetFormData } from './schema';
import type { AssetAttributes, AssetFieldDefinition, AssetWrite } from '@/types/oscar';

const empty: AssetFormData = {
  SerialNumber: '',
  Category: 'Laptop',
  AssetTag: '',
  Make: '',
  Model: '',
  Hostname: '',
  Msisdn: '',
  PurchaseDate: '',
  PurchasePrice: null,
  Supplier: '',
  PoReference: '',
  WarrantyExpiry: '',
  LifecycleState: 'Available at BTG',
  LocationId: null,
  PriorUser: '',
  Notes: '',
};

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

/** Coerce a raw form string to the value type the API expects per DataType. */
function coerce(def: AssetFieldDefinition, raw: string): string | number | boolean | null {
  if (raw === '') return null;
  if (def.DataType === 'Number') { const n = Number(raw); return Number.isNaN(n) ? raw : n; }
  if (def.DataType === 'Boolean') return raw === 'true';
  return raw;
}

export function AssetEditForm() {
  const { id } = useParams();
  const assetId = id ? Number(id) : null;
  const isEdit = assetId !== null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [attrs, setAttrs] = useState<Record<string, string>>({});

  const locationsQuery = useQuery({ queryKey: ['locations', 'stock'], queryFn: ({ signal }) => getLocations({ isStockLocation: true, activeOnly: true }, signal), retry: false });
  const assetQuery = useQuery({ queryKey: ['asset', assetId], queryFn: ({ signal }) => getAsset(assetId!, signal), enabled: isEdit });

  const form = useForm<AssetFormData>({ resolver: zodResolver(assetSchema), defaultValues: empty });
  const { register, handleSubmit, reset, watch, formState } = form;
  const category = watch('Category');
  const isSim = category === 'SIM';

  const defsQuery = useQuery({
    queryKey: ['asset-field-definitions', category],
    queryFn: ({ signal }) => listFieldDefinitions(category, signal),
    enabled: !!category,
  });
  const defs = defsQuery.data ?? [];

  useEffect(() => {
    if (assetQuery.data) {
      const a = assetQuery.data.Asset;
      reset({
        SerialNumber: a.SerialNumber ?? '',
        Category: a.Category,
        AssetTag: a.AssetTag ?? '',
        Make: a.Make ?? '',
        Model: a.Model ?? '',
        Hostname: a.Hostname ?? '',
        Msisdn: a.Msisdn ?? '',
        PurchaseDate: a.PurchaseDate ? a.PurchaseDate.slice(0, 10) : '',
        PurchasePrice: a.PurchasePrice,
        Supplier: a.Supplier ?? '',
        PoReference: a.PoReference ?? '',
        WarrantyExpiry: a.WarrantyExpiry ? a.WarrantyExpiry.slice(0, 10) : '',
        LifecycleState: a.LifecycleState,
        LocationId: a.LocationId,
        PriorUser: a.PriorUser ?? '',
        Notes: a.Notes ?? '',
      });
      const initial: Record<string, string> = {};
      if (a.Attributes) for (const [k, v] of Object.entries(a.Attributes)) initial[k] = v == null ? '' : String(v);
      setAttrs(initial);
    }
  }, [assetQuery.data, reset]);

  function toPayload(data: AssetFormData): AssetWrite {
    const blank = (v?: string) => (v && v.trim() !== '' ? v.trim() : undefined);
    const attributes: AssetAttributes = {};
    for (const def of defs) {
      const raw = attrs[def.FieldKey];
      if (raw !== undefined && raw !== '') attributes[def.FieldKey] = coerce(def, raw);
    }
    return {
      SerialNumber: blank(data.SerialNumber) ?? null,
      Category: data.Category,
      AssetTag: blank(data.AssetTag) ?? null,
      Make: blank(data.Make),
      Model: blank(data.Model),
      Hostname: blank(data.Hostname),
      Msisdn: blank(data.Msisdn) ?? null,
      Attributes: Object.keys(attributes).length > 0 ? attributes : null,
      PurchaseDate: blank(data.PurchaseDate),
      // Clearing a date is not the same as not touching it: an omitted field
      // means "leave alone" server-side, so blanking the box has to say so or
      // the old date survives the save.
      ClearPurchaseDate: blank(data.PurchaseDate) === undefined ? true : undefined,
      PurchasePrice: data.PurchasePrice ?? undefined,
      Supplier: blank(data.Supplier),
      PoReference: blank(data.PoReference),
      WarrantyExpiry: blank(data.WarrantyExpiry),
      ClearWarrantyExpiry: blank(data.WarrantyExpiry) === undefined ? true : undefined,
      LifecycleState: data.LifecycleState,
      LocationId: data.LocationId ?? undefined,
      PriorUser: blank(data.PriorUser) ?? null,
      Notes: blank(data.Notes),
    };
  }

  const mutation = useMutation({
    mutationFn: (data: AssetFormData) => (isEdit ? updateAsset(assetId!, toPayload(data)) : createAsset(toPayload(data))),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['asset', saved.AssetId] });
      navigate(`/assets/${saved.AssetId}`);
    },
    onError: (err) => setSubmitError(err instanceof ApiError ? err.message : 'Could not save the asset.'),
  });

  const locations = locationsQuery.data ?? [];

  return (
    <section className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-navy">{isEdit ? 'Edit asset' : 'Add asset'}</h1>
      {submitError && <div role="alert" className="mt-4 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{submitError}</div>}

      <form onSubmit={handleSubmit((d) => { setSubmitError(null); mutation.mutate(d); })} className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={isSim ? 'ICCID (serial)' : 'Serial number'} error={formState.errors.SerialNumber?.message}><input {...register('SerialNumber')} className={inputCls} /></Field>
          <Field label="Category"><select {...register('Category')} className={inputCls}>{assetCategories.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
          {isSim && <Field label="Mobile number (MSISDN)"><input {...register('Msisdn')} placeholder="+61…" className={inputCls} /></Field>}
          <Field label="Asset tag (ORG-XXXX)" error={formState.errors.AssetTag?.message}><input {...register('AssetTag')} placeholder="ORG-0001" className={inputCls} /></Field>
          <Field label="Make"><input {...register('Make')} className={inputCls} /></Field>
          <Field label="Model"><input {...register('Model')} className={inputCls} /></Field>
          {!isSim && <Field label="Hostname"><input {...register('Hostname')} className={inputCls} /></Field>}
          <Field label="Status">
            <select {...register('LifecycleState')} className={inputCls}>{lifecycleStates.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </Field>
          <Field label="Location" error={formState.errors.LocationId?.message}>
            <select {...register('LocationId', { setValueAs: (v) => (v === '' ? null : Number(v)) })} className={inputCls}>
              <option value="">—</option>
              {locations.map((l) => <option key={l.LocationId} value={l.LocationId}>{l.LocationDesc}</option>)}
            </select>
          </Field>
          <Field label="Purchase date"><input type="date" {...register('PurchaseDate')} className={inputCls} /></Field>
          <Field label="Purchase price"><input type="number" step="0.01" {...register('PurchasePrice', { setValueAs: (v) => (v === '' ? null : Number(v)) })} className={inputCls} /></Field>
          <Field label="Supplier"><input {...register('Supplier')} className={inputCls} /></Field>
          <Field label="PO reference"><input {...register('PoReference')} className={inputCls} /></Field>
          <Field label="Warranty expiry"><input type="date" {...register('WarrantyExpiry')} className={inputCls} /></Field>
        </div>

        {/* Category-specific fields, driven by oscar.AssetFieldDefinition. */}
        {defs.length > 0 && (
          <fieldset className="rounded border border-border bg-bg/40 p-3">
            <legend className="px-1 text-xs font-semibold text-darkGrey">{category} fields</legend>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {defs.map((def) => (
                <Field key={def.FieldDefinitionId} label={def.Label + (def.IsRequired ? ' *' : '')}>
                  {def.DataType === 'Select' ? (
                    <select value={attrs[def.FieldKey] ?? ''} onChange={(e) => setAttrs((s) => ({ ...s, [def.FieldKey]: e.target.value }))} className={inputCls}>
                      <option value="">—</option>
                      {(def.Options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : def.DataType === 'Boolean' ? (
                    <select value={attrs[def.FieldKey] ?? ''} onChange={(e) => setAttrs((s) => ({ ...s, [def.FieldKey]: e.target.value }))} className={inputCls}>
                      <option value="">—</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input
                      type={def.DataType === 'Number' ? 'number' : def.DataType === 'Date' ? 'date' : 'text'}
                      value={attrs[def.FieldKey] ?? ''}
                      onChange={(e) => setAttrs((s) => ({ ...s, [def.FieldKey]: e.target.value }))}
                      className={inputCls}
                    />
                  )}
                </Field>
              ))}
            </div>
          </fieldset>
        )}

        <Field label="Previously held by" error={formState.errors.PriorUser?.message}>
          <input {...register('PriorUser')} className={inputCls} placeholder="Name of the person who had this device" />
          <span className="mt-1 block font-normal text-slate-600">
            Free text, deliberately not linked to a person record — it has to survive
            them leaving. Shows on the register export.
          </span>
        </Field>
        <Field label="Notes"><textarea {...register('Notes')} rows={3} className={inputCls} /></Field>

        <div className="flex gap-2">
          <button type="submit" disabled={mutation.isPending} className="rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create asset'}
          </button>
          <button type="button" onClick={() => navigate(isEdit ? `/assets/${assetId}` : '/assets')} className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-darkGrey hover:bg-bg">Cancel</button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-darkGrey">
      {label}
      {children}
      {error && <span className="mt-1 block font-normal text-red">{error}</span>}
    </label>
  );
}
